import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Plus, Calendar } from "lucide-react";
import { useEvent } from "../lib/event";

export default function EventSwitcher() {
  const { events, currentEvent, setCurrentEventId, loading } = useEvent();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const choose = (id) => { setCurrentEventId(id); setOpen(false); };
  const todayIso = new Date().toISOString().slice(0, 10);
  const isPast = (e) => {
    // we don't have dates inline; lean on date_count + status. For now, treat status==published & recent as Active.
    return (e.status || "").toLowerCase() === "archived";
  };

  return (
    <div className="relative" ref={ref} data-testid="event-switcher">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full text-left px-3 py-2.5 rounded-lg bg-[#0e0e0e] border border-[#1a1a1a] hover:border-[#dc2626] transition-colors"
        data-testid="event-switcher-button"
      >
        <div className="text-[9px] uppercase tracking-[0.2em] text-[#7a7a7a]">Current event</div>
        <div className="flex items-center justify-between mt-0.5">
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate">
              {loading ? "Loading…" : (currentEvent ? currentEvent.name : "No event yet")}
            </div>
          </div>
          <ChevronDown size={16} className="text-[#7a7a7a] flex-shrink-0 ml-2" />
        </div>
      </button>

      {open && (
        <div className="absolute z-50 mt-2 left-0 right-0 st-card p-1.5 max-h-[420px] overflow-y-auto"
             style={{ boxShadow: "0 24px 60px -20px rgba(0,0,0,.7)" }}
             data-testid="event-switcher-panel">
          {events.length === 0 && (
            <div className="p-3 text-xs text-[#8a8a8a]">No events yet.</div>
          )}
          {events.map((e) => {
            const active = currentEvent?.id === e.id;
            return (
              <button key={e.id} onClick={() => choose(e.id)}
                      data-testid={`switcher-item-${e.id}`}
                      className={`w-full text-left rounded-[5px] px-3 py-2.5 border-l-[3px] transition-colors ${active ? "border-[#dc2626] text-[#dc2626]" : "border-transparent hover:border-[rgba(220,38,38,0.4)] hover:text-[rgba(220,38,38,0.75)]"}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{e.name}</div>
                    <div className={`text-[10px] mt-0.5 ${active ? "text-[#dc2626]/70" : "text-[#7a7a7a]"}`}>
                      <Calendar size={10} className="inline mr-1 -mt-0.5" />
                      {e.date_count} dates · {e.status}
                    </div>
                  </div>
                  <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-[5px] ${
                    isPast(e) ? "bg-[#1f1f1f] text-[#8a8a8a]" :
                    active ? "bg-[rgba(220,38,38,0.15)] text-[#dc2626] border border-[rgba(220,38,38,0.4)]" : "bg-[#0f1d12] text-[#86efac] border border-[#1b3a25]"
                  }`}>
                    {isPast(e) ? "Past" : "Active"}
                  </span>
                </div>
              </button>
            );
          })}
          <div className="border-t border-[#1a1a1a] mt-1.5 pt-1.5">
            <button onClick={() => { setOpen(false); nav("/dashboard/events/new"); }}
                    data-testid="switcher-create-new"
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-[#161616] text-[#dc2626] font-semibold text-sm">
              <Plus size={14} /> Create new event
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
